import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { AxiosError } from 'axios'
import type { Inbox } from '@/types/chatwoot'
import { fetchInboxes } from '@/api/inboxes'

const STORAGE_KEY = 'kanban.inboxFilter.v1'

// Filtro: Set vazio significa "todos" (sem filtro). Quando o usuário desmarca
// algum chip, o Set passa a ter os IDs selecionados explicitamente.
export const useInboxesStore = defineStore('inboxes', () => {
  const inboxes = ref<Inbox[]>([])
  const selectedIds = ref<Set<number>>(new Set())
  const loading = ref(false)
  const error = ref<string | null>(null)

  const allSelected = computed(() => selectedIds.value.size === 0)

  const isVisible = (inboxId: number): boolean =>
    allSelected.value || selectedIds.value.has(inboxId)

  function persist() {
    if (selectedIds.value.size === 0) {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(selectedIds.value)),
      )
    }
  }

  function hydrate(): void {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    try {
      const arr = JSON.parse(raw) as number[]
      if (Array.isArray(arr) && arr.every((v) => typeof v === 'number')) {
        selectedIds.value = new Set(arr)
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  function toggle(inboxId: number): void {
    const next = new Set(selectedIds.value)
    if (allSelected.value) {
      // Estado atual = todos. Clicar em um chip significa "só esse".
      next.clear()
      next.add(inboxId)
    } else if (next.has(inboxId)) {
      next.delete(inboxId)
      // Se o usuário desmarca o último, volta pra "todos".
      if (next.size === 0) {
        selectedIds.value = next
        persist()
        return
      }
    } else {
      next.add(inboxId)
      // Se acabou de marcar todos manualmente, normaliza para "todos".
      if (next.size === inboxes.value.length && inboxes.value.length > 0) {
        next.clear()
      }
    }
    selectedIds.value = next
    persist()
  }

  function selectAll(): void {
    selectedIds.value = new Set()
    persist()
  }

  async function load(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      inboxes.value = await fetchInboxes()
    } catch (e) {
      const axiosError = e as AxiosError<{
        errors?: string[]
        message?: string
      }>
      const data = axiosError.response?.data
      error.value =
        data?.errors?.[0] ??
        data?.message ??
        axiosError.message ??
        'Falha ao carregar inboxes'
    } finally {
      loading.value = false
    }
  }

  return {
    inboxes,
    selectedIds,
    allSelected,
    loading,
    error,
    isVisible,
    toggle,
    selectAll,
    hydrate,
    load,
  }
})
